import { useMemo, useRef, useState } from "react";
import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import {
  convertToAssetId,
  getMessageFromNetworkError,
  useDeleteZaps,
  useDisableZap,
  useMoveZapsToFolder,
  useUserAccountRole,
  useZapApprovals,
  useIsApprovalRequiredForZap,
} from "@zapier/client";
import {
  useNotification,
  ApprovalType,
  ApprovalRequiredOperation,
  AssetTypes,
} from "@zapier/utils";
import { Approval } from "@zapier/bff";
import styles from "./ZapDeleteModal.module.css";
import { ZapModal } from "../types";
import { useBulkActionLimitGuard } from "../../../hooks/useBulkActionLimitGuard";
import { TrashNotification } from "../../common/TrashNotification/TrashNotification";
import { UndeletableAssetsList } from "../../common/LinkedAssetsWarning/UndeletableAssetsList";

/** Typed-id result (e.g. "zap:123") from `useBulkCheckDeletePermissions`. */
interface ZapDeletePermissionResult {
  nonDeletableIds: string[];
  isLoading: boolean;
  hasChecked: boolean;
}

interface Props {
  currentItemFolderId?: string;
  currentItemIds: string[];
  enabledZapIds?: string[] | undefined;
  /** The single Zap's title. Only rendered when `currentItemIds.length === 1` — plural copy is driven by `count` instead. */
  currentItemTitle: string;
  isOpen: boolean;
  isZapEnabled: boolean;
  isZapPublished: boolean;
  onSettled?: () => void;
  setApprovalType?: (approvalType: ApprovalType) => void;
  setCurrentModal: (modal?: ZapModal | undefined) => void;
  setIsOpen: (isOpen: boolean) => void;
  projectId?: string;
  folderId?: string; // used for cache invalidation
  /** Omit to skip permission filtering (every selected Zap is deletable). */
  permissionResult?: ZapDeletePermissionResult;
  /** Zap id -> title, for naming the single deletable Zap and the "won't be deleted" list. */
  zapTitlesById?: Record<string, string>;
}

interface ContentProps extends Props {
  onDelete: () => void;
  isApprovalRequired?: boolean;
  isDeleting: boolean;
  pendingDeleteApproval?: Approval;
  userPendingApproval?: boolean;
  isBulk: boolean;
  deletableItemCount: number;
  deletableItemTitle: string;
  undeletableZaps: { id: string; title: string }[];
  isPermissionPending: boolean;
  isNoneDeletable: boolean;
}

interface onDeleteOptions {
  currentItemFolderId: string;
  /** May differ from the modal's current currentItemIds if reopened for another Zap. */
  itemIds: string[];
  /** The "moved to trash" toast, closed once the restore succeeds. */
  trashToastId: string;
}

/** Order-independent id comparison. */
function hasSameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

// An empty shown list doesn't count as "different": a bulk delete prunes its
// own rows from the selection before this runs.
function isShowingDifferentZaps(
  shownItemIds: string[],
  trackedItemIds: string[],
): boolean {
  return shownItemIds.length > 0 && !hasSameIds(shownItemIds, trackedItemIds);
}

interface DeletableSelectionInput {
  currentItemIds: string[];
  permissionResult?: ZapDeletePermissionResult;
}

/** Splits the selection by the permission check; unchecked Zaps count as deletable. */
function splitSelectionByPermission({
  currentItemIds,
  permissionResult,
}: DeletableSelectionInput) {
  const isPermissionPending =
    !!permissionResult &&
    (!permissionResult.hasChecked || permissionResult.isLoading);
  if (!permissionResult || isPermissionPending) {
    return {
      deletableItemIds: currentItemIds,
      undeletableItemIds: [] as string[],
      isPermissionPending,
    };
  }

  const nonDeletableTypedIds = new Set(permissionResult.nonDeletableIds);
  const isDeletable = (id: string) =>
    !nonDeletableTypedIds.has(convertToAssetId(AssetTypes.Zap, id));
  return {
    deletableItemIds: currentItemIds.filter((id) => isDeletable(id)),
    undeletableItemIds: currentItemIds.filter((id) => !isDeletable(id)),
    isPermissionPending,
  };
}

function ZapDeleteModalContent({
  currentItemIds,
  deletableItemCount,
  deletableItemTitle,
  isApprovalRequired,
  isBulk,
  isDeleting,
  isNoneDeletable,
  isOpen,
  isPermissionPending,
  onDelete,
  pendingDeleteApproval,
  setIsOpen,
  undeletableZaps,
  userPendingApproval,
}: ContentProps) {
  const t = useTranslations("zap-delete-modal");
  const tBulk = useTranslations("bulk-delete-modal");
  const { customuserId: userId } = useUserAccountRole();

  const deleteCopy = useMemo(() => {
    if (isNoneDeletable) {
      return t("buttons.move-to-trash", { count: 1 });
    }

    // If delete requires an approval, show request to delete copy
    // Only applies for singular deletes, user cannot request to delete multiple zaps
    if (isApprovalRequired && !isBulk) {
      const requestToDeleteCopy =
        pendingDeleteApproval &&
        pendingDeleteApproval.requestedByUserId === userId
          ? t("buttons.cancel-request-to-delete")
          : t("buttons.request-to-delete");

      return requestToDeleteCopy;
    }

    return t("buttons.move-to-trash", { count: deletableItemCount });
  }, [
    isApprovalRequired,
    pendingDeleteApproval,
    userId,
    t,
    deletableItemCount,
    isBulk,
    isNoneDeletable,
  ]);

  // Blocked while an approval the current user can't act on is pending.
  const isSubmitBlockedByApproval = Boolean(
    (isApprovalRequired &&
      pendingDeleteApproval &&
      pendingDeleteApproval.requestedByUserId !== userId &&
      !isBulk) ||
    (isApprovalRequired &&
      userPendingApproval &&
      !pendingDeleteApproval &&
      !isBulk),
  );
  const isSubmitDisabled =
    isSubmitBlockedByApproval ||
    isDeleting ||
    isPermissionPending ||
    isNoneDeletable;

  const submitDisabledTooltip = isNoneDeletable
    ? tBulk("buttons.delete-permission-denied-tooltip", {
        count: currentItemIds.length,
        assetTypeLabel: "zap",
      })
    : undefined;

  const submitButton = (
    // No onPress: it submits the form, avoiding a double mutation.
    <Button
      variant="danger"
      type="submit"
      isDisabled={isSubmitDisabled}
      isPending={isDeleting}
    >
      {deleteCopy}
    </Button>
  );

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={(event) => {
          // Without this, the browser navigates before the delete resolves.
          event.preventDefault();
          onDelete();
        }}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // Enter already submits via onSubmit; this only blocks it when disabled.
          (event) => {
            if (event.key !== "Enter") {
              return;
            }

            if (isSubmitDisabled) {
              event.preventDefault();
            }
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.move-to-trash-title", {
            isNoneDeletable: String(isNoneDeletable),
            count: deletableItemCount,
            title: deletableItemTitle,
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          {isPermissionPending ? (
            <div className={styles.permissionSpinner}>
              <Spinner />
            </div>
          ) : (
            <div className={styles.body}>
              {!isNoneDeletable && (
                <>
                  <Text>
                    {t("body.deletion-explanation", {
                      count: deletableItemCount,
                    })}
                  </Text>
                  <Spacer height={12} />
                  <Text>{t("body.connected-assets")}</Text>
                </>
              )}
              {undeletableZaps.length > 0 && (
                <>
                  <Spacer height={12} />
                  <Text fontWeight={600}>
                    {tBulk("body.trash.undeletable-intro", {
                      count: undeletableZaps.length,
                      assetTypeLabel: "zap",
                    })}
                  </Text>
                  <Spacer height={8} />
                  <UndeletableAssetsList
                    assets={undeletableZaps.map((zap) => ({
                      id: zap.id,
                      title: zap.title,
                      type: AssetTypes.Zap,
                    }))}
                  />
                </>
              )}
            </div>
          )}
        </ModalDialogBody>
        <ModalDialogFooter>
          {/* Explicit type="button": Cancel must never submit the form. */}
          <Button
            type="button"
            variant="tertiary"
            isDisabled={isDeleting}
            onPress={() => setIsOpen(false)}
          >
            {t("buttons.cancel")}
          </Button>

          {submitDisabledTooltip ? (
            <Tooltip
              allowMultiline
              content={submitDisabledTooltip}
              placement="top"
            >
              <TooltipFocusableTrigger>{submitButton}</TooltipFocusableTrigger>
            </Tooltip>
          ) : (
            submitButton
          )}
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
export const ZapDeleteModal = ({
  currentItemFolderId,
  currentItemIds,
  currentItemTitle,
  projectId,
  enabledZapIds,
  isOpen,
  isZapEnabled,
  isZapPublished,
  onSettled,
  setApprovalType,
  setCurrentModal,
  setIsOpen,
  folderId,
  permissionResult,
  zapTitlesById,
}: Props) => {
  const t = useTranslations("zap-delete-modal");
  const notifyIfOverBulkLimit = useBulkActionLimitGuard("delete");
  const nonDeletableIds = permissionResult?.nonDeletableIds;
  const hasChecked = permissionResult?.hasChecked;
  const isChecking = permissionResult?.isLoading;
  const { deletableItemIds, undeletableItemIds, isPermissionPending } = useMemo(
    () =>
      splitSelectionByPermission({
        currentItemIds,
        permissionResult: nonDeletableIds && {
          nonDeletableIds,
          hasChecked: !!hasChecked,
          isLoading: !!isChecking,
        },
      }),
    [currentItemIds, nonDeletableIds, hasChecked, isChecking],
  );
  // Naming a Zap from the full selection would pick the wrong one when the
  // first selected Zap is the undeletable one.
  const deletableItemTitle =
    deletableItemIds.length === 1
      ? (zapTitlesById?.[deletableItemIds[0]] ??
        (currentItemIds.length === 1 ? currentItemTitle : ""))
      : currentItemTitle;
  const isNoneDeletable =
    !!permissionResult &&
    !isPermissionPending &&
    currentItemIds.length > 0 &&
    deletableItemIds.length === 0;

  const { createNotification, clearNotification } = useNotification();
  const { deleteZapsAsync } = useDeleteZaps({ folderId });
  const { disableZap } = useDisableZap();
  const { moveZapsToFolder } = useMoveZapsToFolder();
  // Tracks ids, not a boolean, so a pending delete doesn't disable the modal
  // after it's reopened for a different Zap (this instance persists across opens).
  const [deletingItemIds, setDeletingItemIds] = useState<string[] | null>(null);
  const isDeleting =
    deletingItemIds !== null &&
    !isShowingDifferentZaps(deletableItemIds, deletingItemIds);

  // Lets a delete settling after a reopen detect it's no longer being shown.
  const currentItemIdsRef = useRef(deletableItemIds);
  currentItemIdsRef.current = deletableItemIds;

  const isBulk = currentItemIds.length > 1;

  const zapId =
    currentItemIds.length === 1 ? String(currentItemIds[0]) : undefined;
  const { pendingDeleteApproval, userPendingApproval } = useZapApprovals({
    zapId,
  });
  const isApprovalRequired = useIsApprovalRequiredForZap({
    zapId,
    isZapPublished,
    operation: ApprovalRequiredOperation.Delete,
  });

  // This callback needs to happen outside of the modal so that it can
  // still display a notification when the request completes after the modal has closed.
  const handleDelete = async () => {
    // Guard before the disable loop, so no zaps are turned off for a delete that can't proceed.
    if (notifyIfOverBulkLimit(deletableItemIds.length)) {
      setIsOpen(false);
      return;
    }

    if (deletableItemIds.length === 0) {
      return;
    }

    if (isApprovalRequired && setApprovalType && !isBulk) {
      setApprovalType(ApprovalType.Delete);
      setCurrentModal(ZapModal.Approval);
      return;
    }

    // If Zap(s) are enabled, disable it before deleting
    if (isZapEnabled && enabledZapIds) {
      const deletableIdSet = new Set(deletableItemIds.map(String));
      for (const zapId of enabledZapIds) {
        if (!deletableIdSet.has(String(zapId))) {
          continue;
        }
        await disableZap({
          zapId,
        });
      }
    }

    // Captured once: the selection could change if the modal reopens mid-delete.
    const itemIdsBeingDeleted = deletableItemIds;

    // Awaited rather than fired with onSuccess/onError, so the modal closes
    // only once the request actually resolves.
    setDeletingItemIds(itemIdsBeingDeleted);
    try {
      const { succeededZapIds, failedZapIds, failureReasons } =
        await deleteZapsAsync({
          zapIds: itemIdsBeingDeleted,
          projectId,
        });

      if (failedZapIds.length === 0) {
        // TODO MNG-4354 - Add undo functionality when deleting project Zaps
        const trashToastId = createNotification({
          title: projectId
            ? t("notifications.success.moved-to-trash-no-undo", {
                count: succeededZapIds.length,
                zapTitle: deletableItemTitle,
              })
            : t("notifications.success.moved-to-trash", {
                count: succeededZapIds.length,
                zapTitle: deletableItemTitle,
              }),
          message: (
            <TrashNotification
              onUndo={
                projectId
                  ? undefined
                  : () =>
                      currentItemFolderId &&
                      handleUndoDelete({
                        currentItemFolderId,
                        itemIds: succeededZapIds,
                        trashToastId,
                      })
              }
            />
          ),
          type: "success",
        });
      } else if (succeededZapIds.length === 0) {
        const isAccessDenied = failedZapIds.some(
          (zapId) => failureReasons?.[zapId] === "access-denied",
        );

        if (isAccessDenied) {
          createNotification({
            title: t("errors.unable-to-delete-title", {
              count: failedZapIds.length,
              zapTitle: deletableItemTitle,
            }),
            message: t("errors.unable-to-delete-message"),
            type: "error",
          });
        } else {
          createNotification({
            message: t("notifications.trash-error"),
            type: "error",
          });
        }
      } else {
        createNotification({
          title: t("errors.unable-to-delete-title", {
            count: failedZapIds.length,
            zapTitle: deletableItemTitle,
          }),
          message: t("notifications.partial", {
            succeededCount: succeededZapIds.length,
            failedCount: failedZapIds.length,
          }),
          type: "warning",
        });
      }
    } catch {
      // Shouldn't normally happen: deleteZaps reports failures in its result
      // rather than rejecting. This guards against a true transport failure.
      createNotification({
        message: t("notifications.trash-error"),
        type: "error",
      });
    } finally {
      // Only clear this delete's own entry, not one started after a reopen.
      setDeletingItemIds((trackedItemIds) =>
        trackedItemIds && hasSameIds(trackedItemIds, itemIdsBeingDeleted)
          ? null
          : trackedItemIds,
      );

      // Skip only when the modal has moved on to a different Zap, which makes
      // this a stale completion for the old one.
      if (
        !isShowingDifferentZaps(currentItemIdsRef.current, itemIdsBeingDeleted)
      ) {
        onSettled?.();
        setIsOpen(false);
      }
    }
  };

  const handleUndoDelete = ({
    currentItemFolderId,
    itemIds,
    trashToastId,
  }: onDeleteOptions) =>
    new Promise<void>((resolve) => {
      moveZapsToFolder(
        {
          folderId: currentItemFolderId,
          zapIds: itemIds,
        },
        {
          onSuccess: () => {
            clearNotification(trashToastId);
            createNotification({
              message: (
                <Text>
                  {t("notifications.success.restored", {
                    count: itemIds.length,
                    zapTitle: deletableItemTitle,
                  })}
                </Text>
              ),
              type: "success",
            });
          },
          onError: (error: any) => {
            createNotification({
              title: t("errors.unable-to-restore-title", {
                count: itemIds.length,
                zapTitle: deletableItemTitle,
              }),
              message: getMessageFromNetworkError(error),
              type: "error",
            });
          },
          onSettled: () => {
            onSettled?.();
            resolve();
          },
        },
      );

      setIsOpen(false);
    });

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <ZapDeleteModalContent
      currentItemIds={currentItemIds}
      currentItemTitle={currentItemTitle}
      deletableItemCount={
        isNoneDeletable ? currentItemIds.length : deletableItemIds.length
      }
      deletableItemTitle={deletableItemTitle}
      isApprovalRequired={isApprovalRequired}
      isBulk={isBulk}
      isDeleting={isDeleting}
      isNoneDeletable={isNoneDeletable}
      isOpen={isOpen}
      isPermissionPending={isPermissionPending}
      undeletableZaps={undeletableItemIds.map((id) => ({
        id,
        title: zapTitlesById?.[id] ?? "",
      }))}
      isZapEnabled={isZapEnabled}
      isZapPublished={isZapPublished}
      onDelete={handleDelete}
      pendingDeleteApproval={pendingDeleteApproval}
      setCurrentModal={setCurrentModal}
      setIsOpen={setIsOpen}
      userPendingApproval={userPendingApproval}
    />
  );
};
